import { DEFAULT_MESSAGES, RESTRICTION_TYPES } from './RequestDetailsTab.constants';

/**
 * Safely formats a date string
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date or N/A
 */
export const formatDate = (date) => {
    if (!date) return DEFAULT_MESSAGES.NOT_AVAILABLE;
    try {
        return new Date(date).toLocaleDateString();
    } catch {
        return DEFAULT_MESSAGES.NOT_AVAILABLE;
    }
};

/**
 * Safely gets a nested property value
 * @param {Object} obj - Object to get value from
 * @param {string} path - Property path (e.g., 'user.email')
 * @param {string} defaultValue - Default value if not found
 * @returns {string} - Property value or default
 */
export const getNestedValue = (obj, path, defaultValue = DEFAULT_MESSAGES.NOT_AVAILABLE) => {
    const keys = path.split('.');
    let result = obj;

    for (const key of keys) {
        if (result && typeof result === 'object' && key in result) {
            result = result[key];
        } else {
            return defaultValue;
        }
    }

    return result || defaultValue;
};

/**
 * Gets restriction chip configuration
 * @param {string} restrictionType - Type of restriction
 * @returns {Object} - Chip configuration
 */
export const getRestrictionChipConfig = (restrictionType) => {
    if (restrictionType === RESTRICTION_TYPES.BANNED) {
        return {
            label: RESTRICTION_TYPES.BANNED,
            color: 'error'
        };
    }
    else if (restrictionType=== RESTRICTION_TYPES.LABELING_REQUIREMENTS) {
        return {
            label: RESTRICTION_TYPES.LABELING_REQUIREMENTS,
            color: 'warning'
        };
    }
    else if (restrictionType === RESTRICTION_TYPES.RESTRICTIONS_AND_LABELING) {
        return {
            label: RESTRICTION_TYPES.RESTRICTIONS_AND_LABELING,
            color: 'warning'
        };
    }
    else if (restrictionType === RESTRICTION_TYPES.RESTRICTED_AND_SDS) {
        return {
            label: RESTRICTION_TYPES.RESTRICTED_AND_SDS,
            color: 'warning'
        };
    }
    return {
        label: RESTRICTION_TYPES.RESTRICTED,
        color: 'warning'
    };
};

/**
 * Extracts request details from requestData
 * @param {Object} requestData - Request data object
 * @returns {Object} - Extracted details
 */
export const extractRequestDetails = (requestData) => {
    if (!requestData?.exitRequest) {
        return null;
    }

    const { exitRequest } = requestData;

    return {
        createdAt: exitRequest.createdAt,
        ingredientName: getNestedValue(exitRequest, 'ingredient.name'),
        casNumber: getNestedValue(exitRequest, 'ingredient.casNumber'),
        restrictionType: exitRequest.restrictionType,
        primaryDate: exitRequest.primaryDate,
        primaryDateType: exitRequest.primaryDateType,
        secondaryDate: exitRequest.secondaryDate,
        secondaryDateType: exitRequest.secondaryDateType,
        ulInternalDate: exitRequest.ulInternalDate,
        issuedBy: getNestedValue(exitRequest, 'user.username'),
        email: getNestedValue(exitRequest, 'user.email', null),
        ingredientDescription: getNestedValue(exitRequest, 'ingredient.description', DEFAULT_MESSAGES.NO_DESCRIPTION),
        additionalInfo: exitRequest.additionalInfo || DEFAULT_MESSAGES.NO_ADDITIONAL_INFO
    };
};